import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const AvatarModal = ({ isOpen, onClose, imageUrl, images = [], altText }) => {
  const [imageError, setImageError] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Combine single imageUrl with images array if needed, or just use images
  // If images array is empty but imageUrl is provided, create an array with one item
  const allImages = images.length > 0 ? images : (imageUrl ? [imageUrl] : []);
  
  // Reset state when modal opens or images change
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
      setImageError(false);
    }
  }, [isOpen, allImages.length]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, allImages.length]); // Dependencies for closure
  
  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleImageError = (e) => {
    console.error('Failed to load large avatar image:', e.target.src);
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageError(false);
  };

  const nextImage = () => {
    if (allImages.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % allImages.length);
    setImageError(false);
  };

  const prevImage = () => {
    if (allImages.length <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
    setImageError(false);
  };

  // Determine which image to show
  const currentSrc = allImages.length > 0 ? allImages[currentIndex] : `${process.env.PUBLIC_URL}/images/bela-avatar-l.jpeg`;
  const fallbackSrc = `${process.env.PUBLIC_URL}/images/bela-avatar-s2.jpeg`;
  const alt = altText || "Bela";

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 z-[60] flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="relative max-w-7xl w-full h-full flex flex-col items-center justify-center pointer-events-none">
        {/* Close button - pointer-events-auto to make it clickable */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 text-white hover:text-gray-300 transition-colors pointer-events-auto bg-black bg-opacity-50 rounded-full p-2"
          aria-label="Close"
        >
          <FiX size={32} />
        </button>

        {/* Navigation Buttons */}
        {allImages.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 text-white hover:text-gray-300 transition-colors pointer-events-auto bg-black bg-opacity-50 rounded-full p-2"
              aria-label="Previous image"
            >
              <FiChevronLeft size={40} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 text-white hover:text-gray-300 transition-colors pointer-events-auto bg-black bg-opacity-50 rounded-full p-2"
              aria-label="Next image"
            >
              <FiChevronRight size={40} />
            </button>
          </>
        )}

        {/* Avatar image */}
        <div className="relative w-full h-full flex items-center justify-center pointer-events-auto">
          {!imageError ? (
            <img 
              src={currentSrc}
              alt={`${alt} ${allImages.length > 1 ? `(${currentIndex + 1}/${allImages.length})` : ''}`}
              className="max-w-full max-h-[90vh] object-contain shadow-2xl rounded-lg"
              onError={handleImageError}
              onLoad={handleImageLoad}
            />
          ) : (
            // Fallback logic
            allImages.length > 0 ? (
                // If a custom URL failed
                <div className="p-8 text-center text-gray-500 bg-white rounded-lg">
                    <p>Image could not be loaded.</p>
                </div>
            ) : (
                // If it was the default Bela image that failed
                <img 
                src={fallbackSrc}
                alt={alt}
                className="max-w-full max-h-[90vh] object-contain"
                onError={(e) => {
                    e.target.style.display = 'none';
                }}
                />
            )
          )}
        </div>

        {/* Image Counter */}
        {allImages.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm pointer-events-auto">
            {currentIndex + 1} / {allImages.length}
          </div>
        )}
      </div>
    </div>
  );
};

export default AvatarModal;
